import { t } from "@lingui/macro";
import { appStore } from "@src/stores/AppStore";
import { minValue, required } from "@src/utils/forms/validators";
import { NumberModel } from "@src/utils/models";
import { FieldState, FormState } from "formstate";
import { computed, makeObservable } from "mobx";
import type { Store } from "../Store";

type TBrand = NonNullable<
  Store["brandCreditAccount"]
>["brand"]["client"]["brands"][0];
type TCreditAccount = NonNullable<TBrand["creditAccounts"]>[0];

export class FormTransferCredit {
  store: Store;
  selectedCreditAccount?: TCreditAccount;

  private requiredCommonBudgetCategory = (creditAccountId: string) => {
    if (!creditAccountId) return undefined;

    const creditAccount = this.getCreditAccountById(creditAccountId);
    if (!creditAccount) return undefined;

    const fromExpenseCategories = (
      this.store.brandCreditAccount?.expenseBudgetCategories || []
    ).map((ctg) => ctg.id);
    const toExpenseCategories = (
      creditAccount.expenseBudgetCategories || []
    ).map((ctg) => ctg.id);
    const commonExpenseCategories = fromExpenseCategories.find((id) =>
      toExpenseCategories.includes(id),
    );
    if (commonExpenseCategories) return undefined;

    const fromOurWorkCategories = (
      this.store.brandCreditAccount?.ourWorkBudgetCategories || []
    ).map((ctg) => ctg.id);
    const toOurWorkCategories = (
      creditAccount.ourWorkBudgetCategories || []
    ).map((ctg) => ctg.id);
    const commonOurWorkCategories = fromOurWorkCategories.find((id) =>
      toOurWorkCategories.includes(id),
    );
    if (commonOurWorkCategories) return undefined;

    return t`Unable to transfer credit due to budget category`;
  };

  private requiredIfTypeIsProject = (value: string) => {
    if (this.type.$ !== "project") return undefined;
    return required(value);
  };

  type = new FieldState<"project" | "account" | undefined>(
    undefined,
  ).validators(required);
  project_id = new FieldState("").validators(this.requiredIfTypeIsProject);
  to_credit_account_id = new FieldState("").validators(
    required,
    this.requiredCommonBudgetCategory,
  );
  amount = new FieldState(NumberModel.fromDenomValue(0, 2)).validators(
    minValue(1),
  );

  form = new FormState([
    this.type,
    this.to_credit_account_id,
    this.project_id,
    this.amount,
  ]);

  constructor(store: Store) {
    makeObservable(this);
    this.store = store;
    this.amount.reset(
      this.amount.value.setDenominator(
        appStore.workspaceStore.settings?.currency.denomination!,
      ),
    );
  }

  @computed get payload() {
    return {
      amount: this.amount.$.denominatedValue,
      to_credit_account_id: this.to_credit_account_id.$,
    };
  }

  getCreditAccountById = (id: string) => {
    if (!this.store.brandCreditAccount) return undefined;

    for (const brand of this.store.brandCreditAccount.brand.client.brands) {
      const creditAccount = brand.creditAccounts?.find(
        (ca) => ca.creditAccount.id === id,
      );
      if (creditAccount) return creditAccount;
    }

    return undefined;
  };

  reset() {
    this.form.reset();
    this.amount.reset(this.amount.value.setValue(0));
  }
}
